import type { ReactNode } from "react";
import { getStrings, homeShellString } from "../i18n/strings";
import type { PublicIpRiskResultDto } from "../types/dto";
import { resolveNetworkIdentityPresentation, withTentativeSuffix } from "../utils/networkIdentity";
import {
  KeyFindingsList,
  NetworkContextBanner,
  RiskSummaryPanel,
  RiskWhyPanel,
  StrongSignalCallout,
} from "./RiskSignalInsights";

type Strings = ReturnType<typeof getStrings>;

// Everything a visitor reads *after* the verdict: the glance tiles (country/type/network type/ASN)
// and the supporting evidence panels (summary/why/key findings/key signals/strong-signal callout).
// The verdict itself — address, the NetRiskScan Index ring, the one-line blurb — lives in
// HomeVisitorIpCard.tsx now, so nothing here restates a band or renders a second gauge; this is
// detail in support of that one verdict, never a rival to it (spec §四E).
export function HomeReportCard({ result, strings }: { result: PublicIpRiskResultDto; strings: Strings }) {
  return (
    <div className="home-report">
      <InfoTiles result={result} strings={strings} />
      {/* The full "recognized public infrastructure" explanation. It used to sit inside the hero
          card, where its height pushed the Index ring and the verdict down (spec §十三); the card
          carries a one-line chip now and the paragraph lands here, in the evidence area it belongs
          to, so nothing about the address is hidden. */}
      <NetworkContextBanner risk={result.risk} strings={strings} />
      <RiskSummaryPanel risk={result.risk} strings={strings} />
      <RiskWhyPanel risk={result.risk} strings={strings} />
      <KeyFindingsList risk={result.risk} strings={strings} />
      <StrongSignalCallout risk={result.risk} torNetwork={result.torNetwork} strings={strings} />
    </div>
  );
}

// Country, network access, connection type and ASN — four short facts for a reader who only wants a
// glance. Deliberately does NOT include a "risk level" tile: that would just restate the ring/badge
// above in smaller type, which is exactly the duplicate-verdict pattern this redesign removes (spec:
// don't show the same level word three times on one page).
//
// network-identity-semantic-v2: previously showed "IP type" and "Network type" side by side, both
// derived from the same underlying Access-axis fact via two different, occasionally-disagreeing
// helpers — the exact "same fact, three fields" duplication the panel below already avoided. Now one
// Access tile and one Connection tile, sourced from the single presentation resolver.
function InfoTiles({ result, strings }: { result: PublicIpRiskResultDto; strings: Strings }) {
  const t = <K extends Parameters<typeof homeShellString>[1]>(key: K) => homeShellString(strings, key);
  const identity = resolveNetworkIdentityPresentation(result, strings);
  const accessValue = identity.access.label ? withTentativeSuffix(identity.access.label, identity.access.state, strings) : null;
  const connectionValue = identity.connection.label
    ? withTentativeSuffix(identity.connection.label, identity.connection.state, strings)
    : null;

  const tiles: Array<{ key: string; icon: TileIconType; label: string; value: string | null }> = [
    { key: "country", icon: "globe", label: strings.fieldCountry, value: result.location.countryName },
    { key: "access", icon: "monitor", label: strings.homeFactNetworkAccess ?? strings.fieldAccessType ?? "Network access", value: accessValue },
    { key: "connection", icon: "shield", label: strings.fieldConnectionType, value: connectionValue },
    { key: "asn", icon: "monitor", label: t("homeRowAsn"), value: result.network.asn },
  ].filter((tile): tile is { key: string; icon: TileIconType; label: string; value: string } => Boolean(tile.value));

  if (tiles.length === 0) return null;

  return (
    <div className="home-tiles">
      {tiles.map((tile) => (
        <div className="home-tile" key={tile.key}>
          <span className="home-tile__icon" aria-hidden="true"><InfoTileIcon type={tile.icon} /></span>
          <span className="home-tile__body">
            <span className="home-tile__label">{tile.label}</span>
            <span className="home-tile__value">{tile.value}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

// The facts a visitor only wants after the verdict. Rows whose field the provider didn't return are
// not rendered — an empty value here would read as "we checked and found nothing" rather than "this
// provider doesn't carry that field".
export function HomeNetworkPanel({ result, strings }: { result: PublicIpRiskResultDto; strings: Strings }) {
  const t = <K extends Parameters<typeof homeShellString>[1]>(key: K) => homeShellString(strings, key);
  const location = [result.location.cityName, result.location.regionName, result.location.countryName].filter(Boolean).join(", ");
  const identity = resolveNetworkIdentityPresentation(result, strings);
  const accessValue = identity.access.label ? withTentativeSuffix(identity.access.label, identity.access.state, strings) : null;
  const connectionValue = identity.connection.label
    ? withTentativeSuffix(identity.connection.label, identity.connection.state, strings)
    : null;
  // network-identity-semantic-v2: Infrastructure is its own row, shown only when there is something
  // meaningful to say (detected, or explicitly checked-and-not-found) — never for "unknown", where
  // silence is the honest answer (spec: null must not render as false).
  const infrastructureValue = identity.infrastructure.state === "unknown" ? null : identity.infrastructure.label;

  const facts = [
    { label: t("homeRowAsn"), value: result.network.asn },
    { label: strings.fieldIsp, value: result.network.isp ?? result.network.organization },
    { label: strings.fieldAccessType ?? t("homeRowIpType"), value: accessValue },
    { label: strings.fieldConnectionType, value: connectionValue },
    { label: strings.fieldInfrastructure ?? "Infrastructure", value: infrastructureValue },
    { label: t("homeRowLocation"), value: location || null },
    { label: strings.fieldHostname, value: result.network.hostname },
    { label: t("homeRowNetworkRange"), value: result.network.networkRange },
  ].filter((fact): fact is { label: string; value: string } => isPresentableValue(fact.value));

  if (facts.length === 0) return null;

  return (
    <HomePanel icon="network" title={t("homeSectionNetwork")}>
      <dl className="home-facts">
        {facts.map((fact) => (
          <div key={fact.label} className="home-fact">
            <dt>{fact.label}</dt>
            <dd>{fact.value}</dd>
          </div>
        ))}
      </dl>
    </HomePanel>
  );
}

/// The shared shell for everything below the hero card: one thin title line, then content. Kept in
/// this module so the network, environment and map panels cannot drift apart visually.
export function HomePanel({ icon, title, children }: { icon: PanelIconType; title: string; children: ReactNode }) {
  return (
    <section className="home-panel">
      <h2 className="home-panel__title"><PanelIcon type={icon} />{title}</h2>
      {children}
    </section>
  );
}

/// Providers sometimes fill a field with a literal placeholder rather than leaving it null. Showing
/// "Unknown" or "-" next to a label costs a row and tells the reader nothing, so those count as
/// absent here and the row is dropped entirely. Exported because the hero card applies exactly the
/// same rule to the identity line it renders above this panel (see HomeVisitorIpCard.tsx) — one
/// definition of "the provider did not actually give us this", not two that can drift.
export function isPresentableValue(value: string | null | undefined): value is string {
  if (!value) return false;
  const normalised = value.trim().toLowerCase();
  return normalised.length > 0 && !["-", "--", "n/a", "na", "unknown", "none", "null"].includes(normalised);
}

export type PanelIconType = "network" | "shield" | "alert-shield" | "map";

function PanelIcon({ type }: { type: PanelIconType }) {
  const paths: Record<PanelIconType, ReactNode> = {
    network: <><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M8 8h.01M8 12h.01M8 16h.01M12 8h4M12 12h4M12 16h4" /></>,
    shield: <><path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" /><path d="m9 12 2 2 4-4" /></>,
    // Same shield silhouette as above so the two panels read as a set, with an exclamation instead of
    // a tick — the risk panel states findings, it doesn't certify anything.
    "alert-shield": <><path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" /><path d="M12 8.5v4M12 15.5h.01" /></>,
    map: <><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></>,
  };
  return (
    <svg className="home-panel__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[type]}
    </svg>
  );
}

type TileIconType = "globe" | "monitor" | "shield-check" | "shield";

function InfoTileIcon({ type }: { type: TileIconType }) {
  const paths: Record<TileIconType, ReactNode> = {
    globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></>,
    monitor: <><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></>,
    "shield-check": <><path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" /><path d="m9 12 2 2 4-4" /></>,
    shield: <path d="M12 3 5 6v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6l-7-3Z" />,
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[type]}
    </svg>
  );
}
